import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import Typography from "../../../../common-components/Typography";
import { font } from "../../../../constants/font";
import { AnimationContainer, Scroll, TextAnimOption, TextWrapper } from "../../panel-components";
import { updateTimelineTime } from "../../../../redux/actions/timelineUtils";
import usePanelScrollHeight from "../../../../helper/hooks/useScrollHeight";
import vmTheme from "../../../../constants/theme";
import { EFFECT_TYPES } from "./effects-data";
import { STATIC_PATH } from "../../../../constants/config";
import content from "../../../../constants/content";
import usePreventScrollOnArrowKeys from "../../../../helper/hooks/usePreventScrollOnArrowKeys";
import useMobileDetection from "../../../../helper/hooks/useMobileDetection";
import useNotify from "../../../../helper/hooks/useNotify";

const AnimationEffects = ({ textEffect, type, selectedEffect, effectName, isText }) => {
  const [selectedIcon, setSelectedIcon] = useState({
    effect: effectName
  });
  const [filteredEffect, setFilteredEffect] = useState([]);
  const dispatch = useDispatch();
  const { warn } = useNotify();
  const panelRef = useRef(null);
  const theme = useSelector((state) => state.app.get("theme"));
  const selectedItem = useSelector((state) => {
    return state.app.get("selectedItems");
  });

  const workspaceItems = useSelector((state) => {
    return state.projectDetails.get("workspaceItems");
  });

  const item = useSelector((state) => {
    return state.projectDetails.getIn([
      "workspaceItems",
      selectedItem.get(0)
    ]);
  });

  const workspaceStage = useSelector((state) =>
    state.app.get("workspaceStage")
  );

  const mainScrollableHeight = usePanelScrollHeight({
    panelRef,
    workspaceStage,
    shouldUpdate: true,
  });
  const preventScrollOnArrowKeys = usePreventScrollOnArrowKeys();

  const enterStart = item ? item.get("enterStart") : 0;
  const exitEnd = item ? item.get("exitEnd") : 0;
  const enterEnd = item ? item.get("enterEnd") : 0;
  const exitStart = item ? item.get("exitStart") : 0;
  const duration = item ? (item.get("exitEnd") - item.get("enterStart")) : 0;
  const percentage = 30;
  const result = (percentage / 100) * duration;
  const animDuration = duration < 2 ? result : 1;
  const isMobile = useMobileDetection();

  const getRelatedItemsToUpdate = (selectedId) => {
    if (!selectedId || !workspaceItems) return [selectedId];

    const currentItem = workspaceItems.get(selectedId);
    if (!currentItem) return [selectedId];

    const isReframeParent = currentItem.get("isReframe") === true;
    const originalId = currentItem.get("originalId") || currentItem.get("dropId");
    const isReframeChild = !!currentItem.get("sceneData") && !!originalId;

    if (isReframeParent || isReframeChild) {
      const parentId = isReframeParent ? selectedId : originalId;
      const ids = [parentId];

      workspaceItems.forEach((wsItem) => {
        const wsItemId = wsItem.get("id");
        if (wsItemId === parentId) return;

        const wsOriginalId = wsItem.get("originalId") || wsItem.get("dropId");
        if (
          wsItem.get("sceneData") &&
          wsOriginalId === parentId
        ) {
          ids.push(wsItemId);
        }
      });
      return ids;
    }

    return [selectedId];
  };

  const handleEffectName = (effect, src) => {
    if (!item) return;
    const exitEffectName = item.get("exitEffectName");
    const enterEffectName = item.get("enterEffectName");
    const relatedIds = getRelatedItemsToUpdate(selectedItem.get(0));
    const relatedItems = relatedIds
      .map((id) => workspaceItems?.get(id))
      .filter(Boolean);

    const hasBlockingEnterTransition = relatedItems.some((wsItem) =>
      wsItem.get("transitionExitId") &&
      wsItem.get("transitionExitId") !== "none" &&
      wsItem.get("transitionExitEffect") &&
      wsItem.get("transitionExitEffect") !== "none_transition"
    );

    const hasBlockingExitTransition = relatedItems.some((wsItem) =>
      wsItem.get("transitionEnterId") &&
      wsItem.get("transitionEnterId") !== "none" &&
      wsItem.get("transitionEnterEffect") &&
      wsItem.get("transitionEnterEffect") !== "none_transition"
    );

    if (type === EFFECT_TYPES.ENTER && hasBlockingEnterTransition) {
      warn(content.UNABLE_TO_ADD_ENTER_EFFECT);
      return;
    }
    if (type === EFFECT_TYPES.EXIT && hasBlockingExitTransition) {
      warn(content.UNABLE_TO_ADD_EXIT_EFFECT);
      return;
    }

    if ((exitStart !== enterStart || exitEffectName === "no_Effect") && (enterEnd !== exitEnd || enterEffectName === "no_Effect")) {
      selectedEffect(type, src)
      setSelectedIcon({ effect, src })
      let toUpdate;
      if (type === EFFECT_TYPES.ENTER) {
        const newTime = (exitEffectName !== "no_Effect" && exitStart < enterStart + animDuration)
          ? exitStart
          : enterStart + animDuration;
        toUpdate = {
          enterEffectName: effect,
          enterEnd: (enterEnd > 0 && enterEnd > enterStart && enterEffectName && enterEffectName !== "no_Effect") ? enterEnd : newTime,
          enterEffectIcon: src
        };

      } else {
        const newTime = enterEffectName !== "no_Effect" && enterEnd > exitEnd - animDuration
          ? enterEnd : exitEnd - animDuration

        toUpdate = {
          exitEffectName: effect,
          exitStart: (exitStart > 0 && exitEnd > exitStart && exitEffectName && exitEffectName !== "no_Effect") ? exitStart : newTime,
          exitEffectIcon: src
        };
      }

      const toUpdatePayload = relatedIds.map((id) => ({
        container: "workspaceItems",
        id,
        toUpdate,
      }));

      dispatch(
        updateTimelineTime({
          toUpdate: toUpdatePayload,
        })
      );
    }
  };

  useEffect(() => {
    if (effectName) {
      setSelectedIcon(prevState => ({
        ...prevState,
        effect: effectName
      }));
    }
  }, [effectName, type])

  useEffect(() => {
    if (isText) {
      setFilteredEffect(textEffect)
    } else {
      const data = textEffect.filter((data) => {
        return (data.type !== content.NEW_TEXT_ANIMATIONS);
      })
      setFilteredEffect(data)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isText, type])

  return (
    <Scroll
      height={isMobile ? "330px" : mainScrollableHeight}
      ref={panelRef}
      onKeyDown={preventScrollOnArrowKeys}
      tabIndex={0}
    >
      <AnimationContainer>
        {filteredEffect.map((data) => {
          return (
            <>
              <Typography
                content={data.type}
                color={vmTheme[theme].panelPrimaryColor}
                font={font.boldBase}
                padding="16px 0px 0px"
                className="effectTitle"
              />
              <TextAnimOption gap={"15px"}>
                {data.effects.map((animName) => {
                  return (
                    <TextWrapper
                      key={animName}
                      iconSize={"60px"}
                      onClick={() => handleEffectName(animName.effect, animName.src)}
                      selectedIcon={selectedIcon && animName.effect === selectedIcon.effect} >
                      <img src={`${STATIC_PATH}${animName.src}`} alt="" />
                      <Typography
                        content={animName.text}
                        font={font.normalMicro}
                        color={vmTheme[theme].panelPrimaryColor}
                        enableTrim={false}
                        display="block"
                        align="center"
                        cursor="pointer"
                      />
                    </TextWrapper>
                  );
                })}
              </TextAnimOption>
            </>
          );
        })}
      </AnimationContainer>
    </Scroll>
  );
};

AnimationEffects.propTypes = {
  textEffect: PropTypes.array,
  type: PropTypes.string,
  selectedEffect: PropTypes.func,
  effectName: PropTypes.string,
  isText: PropTypes.bool
};

export default AnimationEffects;